import { useRef, useState } from 'react';

import { WEB_GL_DISABLED_WARNING_MESSAGE } from '@config/constants';
import { useGeolocation } from '@domain/geolocation/GeolocationContext';
import { useMapLayers } from '@domain/map/MapLayersContext';
import { MapboxExpectedError, useNewMap } from '@domain/map/useNewMap';
import { useIsTabletScreenWidthOrThinner } from '@hooks/useScreenWidth';
import { useUserInfo } from '@domain/user/useUserInfo';
import { ZoomNotification } from '../../../views/Explore/components/ZoomNotification/ZoomNotification';
import { useExplore } from '../../../views/Explore/context/ExploreContext';
import {
  getMonthArrayFromUrl,
  getSpeciesIdArrayFromUrl,
} from '../../../views/Explore/context/helpers';
import { useUpdateExploreUrlFromUserInteraction } from '../../../views/Explore/hooks/useUpdateExploreUrlFromUserInteraction';
import { ErrorBanner } from '../../ErrorBanner/ErrorBanner';
import NextImage from 'next/image';
import MapboxErrorBoundary from '../../MapboxErrorBoundary/MapboxErrorBoundary';
import { MobileSearch } from '../../SearchBoxMapContext/components/MobileSearch';
import { SearchBoxMapContext } from '../../SearchBoxMapContext/SearchBoxMapContext';
import crosshair from '../assets/crosshair.png';
import { getInitialZoomLevel } from '../helpers/getInitialZoomLevel';
import { handleCatchIconClick } from '../helpers/handleCatchIconClick';
import { handlePointOfInterestClick } from '../helpers/handlePointOfInterestClick';
import { handleWaterIconClick } from '../helpers/handleWaterIconClick';
import { handleWaypointIconClick } from '../helpers/handleWaypointIconClick';
import { useMapResize } from '../hooks/useMapResize';
import { CATCH_POSITIONS_LAYER } from '../mapData/mapLayers/catchPositions';
import {
  EMPTY_FISHING_WATERS_LAYER,
  FISHING_WATERS_LAYER,
  FORBIDDEN_FISHING_WATERS_LAYER,
} from '../mapData/mapLayers/fishingWaters';
import { POINTS_OF_INTEREST_LAYERS } from '../mapData/mapLayers/pointsOfInterest';
import { WAYPOINTS_LAYERS } from '../mapData/mapLayers/waypoints';
import { FishbrainLayer } from '../types/types';

import { MapIconClickHandler } from './MapIconClickHandler';
import { DEFAULT_COORDINATES } from '../constants/constants';

const MainMap = () => {
  useMapResize();

  const { isPremium } = useUserInfo();

  const { exploreQueryParams } = useExplore();
  const { latitude, longitude, drawerView, catchFiltersMonths, catchFiltersSpecies } =
    exploreQueryParams;
  const catchesFilterMonthArray = getMonthArrayFromUrl(catchFiltersMonths);
  const catchesFilterSpeciesIdArray = getSpeciesIdArrayFromUrl(catchFiltersSpecies);

  const { location: userLocation } = useGeolocation();
  const userCoordinates = userLocation?.coordinates ?? DEFAULT_COORDINATES;
  const mapCoordinates = {
    latitude: latitude ?? userCoordinates.latitude,
    longitude: longitude ?? userCoordinates.longitude,
  };
  const zoom = getInitialZoomLevel(exploreQueryParams);

  const mapRef = useRef<HTMLDivElement>(null);

  const { layers } = useMapLayers();

  useUpdateExploreUrlFromUserInteraction();

  const activeLayers = Object.entries(layers)
    .filter(([, { isWholeLayerVisible }]) => isWholeLayerVisible)
    .map(([layer]) => layer);

  const { error: mapError } = useNewMap(mapRef, zoom, mapCoordinates, {
    fibLayers: activeLayers as FishbrainLayer[],
    catchFilters: { months: catchesFilterMonthArray, speciesIds: catchesFilterSpeciesIdArray },
    mapStyle: exploreQueryParams.mapStyle,
  });
  const [isActive, setIsActive] = useState(false);
  const isTabletScreenWidthOrThinner = useIsTabletScreenWidthOrThinner();

  if (mapError === MapboxExpectedError.WebGLDisabled) {
    return (
      <div className="mx-auto my-6 max-w-(--breakpoint-md)">
        <ErrorBanner errorType="webGLDisabled" errors={[WEB_GL_DISABLED_WARNING_MESSAGE]} />
      </div>
    );
  }

  return (
    <div className="relative grid w-full grid-cols-1 grid-rows-1">
      {(drawerView === 'regulation' || drawerView === 'regulations') && (
        <div className="z-mid pointer-events-none absolute top-1/2 left-1/2 -translate-x-2/4 -translate-y-2/4">
          <NextImage unoptimized src={crosshair} alt="crosshair" width={204} height={204} />
        </div>
      )}
      <div className="col-start-1 col-end-1 row-start-1 row-end-1 w-full" ref={mapRef} />
      <div className="z-high col-start-1 col-end-1 row-start-1 row-end-1 m-auto mt-0 ml-0 h-10 md:mt-4 md:ml-4">
        {isTabletScreenWidthOrThinner ? (
          <MobileSearch setIsActive={setIsActive} isActive={isActive}>
            <SearchBoxMapContext
              placeholder="Search for a fishing water or location..."
              parent="map"
              isActive={isActive}
              setIsActive={setIsActive}
              isMobile
            />
          </MobileSearch>
        ) : (
          <SearchBoxMapContext
            placeholder="Search for a fishing water or location..."
            parent="map"
            isActive={isActive}
            setIsActive={setIsActive}
          />
        )}
      </div>
      {[FISHING_WATERS_LAYER, EMPTY_FISHING_WATERS_LAYER, FORBIDDEN_FISHING_WATERS_LAYER].map(
        fishingWaterLayer => (
          <MapIconClickHandler
            key={fishingWaterLayer.id}
            layer={fishingWaterLayer}
            handleMapIconClick={handleWaterIconClick}
            highlightLayer="fishingWaters"
          />
        ),
      )}
      <MapIconClickHandler
        layer={CATCH_POSITIONS_LAYER}
        handleMapIconClick={handleCatchIconClick}
        highlightLayer="catchPositions"
      />
      {Object.values(POINTS_OF_INTEREST_LAYERS).map(poiIconLayer => (
        <MapIconClickHandler
          key={poiIconLayer.id}
          layer={poiIconLayer}
          handleMapIconClick={handlePointOfInterestClick}
          highlightLayer="pointsOfInterest"
        />
      ))}
      {Object.values(WAYPOINTS_LAYERS).map(waypointLayer => (
        <MapIconClickHandler
          key={waypointLayer.id}
          layer={waypointLayer}
          handleMapIconClick={handleWaypointIconClick}
          highlightLayer="waypoints"
        />
      ))}
      <ZoomNotification isPremium={isPremium} initialZoom={zoom} />
    </div>
  );
};

const MainMapWrapper = () => (
  <MapboxErrorBoundary>
    <MainMap />
  </MapboxErrorBoundary>
);

// Default export to allow for the wrapper

export default MainMapWrapper;
